import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button, TextField, Typography, Box, Autocomplete } from '@mui/material';
import { API_BASE_URL } from '../api/contests';
import { getContests } from '../api/contests';
import PassphraseCheck from './PassphraseCheck';

// Define the FormField and Participant interfaces
type FormField = {
  field_name: string;
  display_label: string;
  data_type: string;
  required: boolean;
};

interface Participant {
  participant_id: number;
  name: string;
}

interface Contest {
  contest_id: number;
  name: string;
}

const evidenceUrlField: FormField = {
  field_name: 'evidence_url',
  display_label: 'введите ссылку на видео',
  data_type: 'text',
  required: false,
};

const AddContestEntry: React.FC = () => {
  const [formFields, setFormFields] = useState<FormField[]>([]);
  const [formData, setFormData] = useState<{ [key: string]: any }>({});
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);
  const [contests, setContests] = useState<Contest[]>([]);
  const [selectedContest, setSelectedContest] = useState<Contest | null>(null);
  const navigate = useNavigate();
  const contestId = selectedContest?.contest_id;
  const contestName = selectedContest?.name;
  const [accessGranted, setAccessGranted] = useState(false);

  useEffect(() => {
    getContests().then((data) => setContests(data));
  }, []);

  useEffect(() => {
    if (contestId) {
      axios
        .get(`${API_BASE_URL}/contests/${contestId}`)
        .then((response) => {
          const metadata = response.data?.form_metadata;
          const parsedMetadata = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
          const fields = parsedMetadata.fields?.filter((f: any) => !f.non_entry_field) || [];
          if (fields?.length > 0) fields.push(evidenceUrlField);
          setFormFields(fields);
        })
        .catch((error) => console.error('Error fetching form metadata:', error));
    }
  }, [contestId]);

  const handleInputChange = (name: string, value: any) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = () => {
    if (!selectedParticipant || !selectedContest) {
      alert('Please select a participant and a contest.');
      return;
    }

    axios
      .post(`${API_BASE_URL}/contest/${contestId}/entry`, {
        participant_id: selectedParticipant.participant_id,
        entry_data: formData,
      })
      .then(() => {
        alert('Entry added successfully!');
        // navigate(`/contest/${contestId}`);
      })
      .catch((error) => console.error(error));
  };

  const renderField = (field: FormField) => {
    switch (field.data_type) {
      case 'number':
        return (
          <TextField
            key={field.field_name}
            label={field.display_label}
            type="number"
            variant="outlined"
            fullWidth
            required={field.required}
            onChange={(e) => handleInputChange(field.field_name, e.target.value)}
            style={{ marginBottom: '16px' }}
          />
        );
      case 'text':
        return (
          <TextField
            key={field.field_name}
            label={field.display_label}
            type="text"
            variant="outlined"
            fullWidth
            required={field.required}
            onChange={(e) => handleInputChange(field.field_name, e.target.value)}
            style={{ marginBottom: '16px' }}
          />
        );
      case 'time':
        return (
          <TextField
            key={field.field_name}
            label={field.display_label}
            type="text"
            variant="outlined"
            fullWidth
            required={field.required}
            placeholder="hh:mm:ss"
            value={formData[field.field_name] || ''}
            onChange={(e) => {
              const value = e.target.value;
              const timeFormat = /^(\d{0,2}):?(\d{0,2})?:?(\d{0,2})?$/;
              if (timeFormat.test(value)) {
                handleInputChange(field.field_name, value);
              }
            }}
            helperText="Введите время в формате hh:mm:ss"
            style={{ marginBottom: '16px' }}
          />
        );
      default:
        return null;
    }
  };

  return (
    !accessGranted ? <PassphraseCheck onAccessGranted={() => setAccessGranted(true)} />
    : <Box>
      <Autocomplete
        options={contests}
        getOptionLabel={(option) => option.name}
        onInputChange={(event, value) => {
          if (!value) {
            setSelectedContest(null);
            setFormFields([]);
          }
        }}
        onChange={(event, value) => setSelectedContest(value)}
        renderInput={(params) => <TextField {...params} label="Search Contest" variant="outlined" />}
        value={selectedContest}
        fullWidth
        style={{ marginBottom: '16px' }}
      />

      {selectedContest && (
        <>
          <Typography variant="h5" gutterBottom>
            Добавить участника в {contestName}
          </Typography>

          <Autocomplete
            options={participants}
            getOptionLabel={(option) => option.name}
            onInputChange={(event, value) => {
              axios
                .get(`${API_BASE_URL}/participants/search?q=${value}`)
                .then((response) => setParticipants(response.data))
                .catch((error) => console.error(error));
            }}
            onChange={(event, value) => setSelectedParticipant(value)}
            renderInput={(params) => <TextField {...params} label="Search Participant" variant="outlined" />}
            value={selectedParticipant}
            fullWidth
            style={{ marginBottom: '16px' }}
          />

          {formFields.map((field) => (
            <Box mb={2} key={field.field_name}>
              {renderField(field)}
            </Box>
          ))}

          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!selectedParticipant || formFields.some((field) => field.required && !formData[field.field_name])}
          >
            Submit Entry
          </Button>
        </>
      )}
    </Box>
  );
};

export default AddContestEntry;
